/// <amd-module name="Build/Admin/Pool/Components/ElasticPoolLogList" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";

import { Ago } from "VSSUI/Ago";
import { Status, Statuses, IStatusProps, StatusSize } from "VSSUI/Status";
import { Table, ITableColumn, SimpleTableCell, TableColumnStyle } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { ElasticPoolLog, LogLevel, OperationType } from "DistributedTask/Client/Contracts/TaskAgent";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IElasticPoolLogListProps {
    logs: ElasticPoolLog[];
}

export class ElasticPoolLogList extends VssComponent<IElasticPoolLogListProps> {
    public render(): JSX.Element {
        return (
            <Table<ElasticPoolLog>
                className="pipelines-elastic-pool-log-list"
                columns={this._getColumns()}
                itemProvider={new ArrayItemProvider<ElasticPoolLog>(this.props.logs)}
            />
        );
    }

    private _getColumns(): ITableColumn<ElasticPoolLog>[] {
        // If we hide the agent column, evenly distribute the agent column width across the other columns
        const levelColumnWidth: number = -3;
        const timestampColumnWidth: number = -12;
        const operationColumnWidth: number = -10;
        const messageColumnWidth: number = -75;

        return  [
            {
                id: "level",
                columnStyle: TableColumnStyle.Secondary,
                width: new ObservableValue(levelColumnWidth),
                renderCell: this._renderLevelCell
            } as ITableColumn<ElasticPoolLog>,
            {
                id: "timestamp",
                name: Resources.Timestamp,
                columnStyle: TableColumnStyle.Secondary,
                width: new ObservableValue(timestampColumnWidth),
                renderCell: this._renderTimestampCell
            } as ITableColumn<ElasticPoolLog>,
            {
                id: "operation",
                name: Resources.Operation,
                columnStyle: TableColumnStyle.Secondary,
                width: new ObservableValue(operationColumnWidth),
                renderCell: this._renderOperationCell
            } as ITableColumn<ElasticPoolLog>,
            {
                id: "message",
                name: Resources.Message,
                columnStyle: TableColumnStyle.Primary,
                width: new ObservableValue(messageColumnWidth),
                renderCell: this._renderMessageCell
            } as ITableColumn<ElasticPoolLog>
        ];
    }

    private _renderLevelCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ElasticPoolLog>, tableItem: ElasticPoolLog): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Status {...this._getStatusPropsForLog(tableItem)} size={StatusSize.m} />
            </SimpleTableCell>
        );
    }

    private _getStatusPropsForLog(log: ElasticPoolLog): IStatusProps {
        switch (log.level) {
            case LogLevel.Error:
                return Statuses.Failed;
            case LogLevel.Warning:
                return Statuses.Warning;
            case LogLevel.Info:
            default:
                return Statuses.Information;
        }
    }

    private _renderTimestampCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ElasticPoolLog>, tableItem: ElasticPoolLog): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="text-ellipsis bolt-list-cell-text">
                {
                    tableItem.timestamp &&
                    <Ago date={tableItem.timestamp} format={AgoFormat.Extended} />
                }
                </span>
            </SimpleTableCell>
        );
    }

    private _renderOperationCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ElasticPoolLog>, tableItem: ElasticPoolLog): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis bolt-list-cell-text">
                        {this._getOperationName(tableItem)}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _getOperationName(log: ElasticPoolLog): string {
        switch (log.operation) {
            case OperationType.ConfigurationJob:
                return Resources.ConfigurationJobOperation;
            case OperationType.SizingJob:
                return Resources.SizingJobOperation;
            case OperationType.IncreaseCapacity:
                return Resources.IncreaseCapacityOperation;
            case OperationType.Reimage:
                return Resources.ReimageOperation;
            case OperationType.DeleteVMs:
                return Resources.DeleteVMsOperation;
            default:
                return "";
        }
    }

    private _renderMessageCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ElasticPoolLog>, tableItem: ElasticPoolLog): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis bolt-list-cell-text">
                        {tableItem.message}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }
}
